import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, Map as MapIcon, Search } from "lucide-react";
import api from "@/lib/api";
import { CITIES } from "@/lib/brand";
import { PropertyCard } from "@/components/PropertyCard";
import { PropertyMap } from "@/components/PropertyMap";
import { Seo } from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";

const TYPES = ["Apartment", "Office", "Shop", "Villa", "Single Family", "Plot", "Apartment Building", "Condominium", "Hotel"];

export default function Listings() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid");
  const [properties, setProperties] = useState(null);
  const [cities, setCities] = useState(CITIES);

  useEffect(() => {
    api.get("/cities").then((r) => { if (r.data?.length) setCities(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (city) params.city = city;
    if (type) params.property_type = type;
    if (q.trim()) params.q = q.trim();
    const t = setTimeout(() => {
      api.get("/properties", { params }).then((r) => setProperties(r.data)).catch(() => setProperties([]));
    }, 250);
    return () => clearTimeout(t);
  }, [status, city, type, q]);

  const markers = useMemo(
    () => (properties || []).filter((p) => p.lat && p.lng).map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, title: p.title })),
    [properties]
  );

  const tabs = [
    { key: "all", label: "All", testid: "filter-tab-all" },
    { key: "rent", label: "For Rent", testid: "filter-tab-rent" },
    { key: "sale", label: "For Sale", testid: "filter-tab-buy" },
  ];

  return (
    <div data-testid="listings-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        path="/listings"
        title="Properties for Sale & Rent in Mombasa"
        description="Browse the full SBS Properties portfolio — apartments, offices, shops, villas and land for sale and rent across Mombasa and the Kenyan coast."
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <FadeUp>
          <Eyebrow>Portfolio</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">All Properties</h1>
          <p className="mt-4 max-w-xl text-muted-foreground">
            Browse our complete portfolio of properties for sale and rent across Mombasa and the Kenyan coast.
          </p>
        </FadeUp>

        {/* Filter bar */}
        <FadeUp delay={0.1} className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4 border hairline bg-white p-4">
          <div className="flex border hairline">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => setStatus(t.key)}
                className={`px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] font-bold transition-colors duration-300 ${
                  status === t.key ? "bg-brand text-ivory" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <select
            data-testid="filter-city-select"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="border hairline bg-ivory px-4 py-2.5 text-sm outline-none"
          >
            <option value="">All Locations</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            data-testid="filter-type-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border hairline bg-ivory px-4 py-2.5 text-sm outline-none"
          >
            <option value="">All Types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>

          <div className="flex flex-1 items-center gap-2 border hairline bg-ivory px-4 py-2.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              data-testid="filter-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or location…"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>

          <button
            data-testid="view-toggle-btn"
            onClick={() => setView(view === "grid" ? "map" : "grid")}
            className="flex items-center gap-2 border hairline px-4 py-2.5 text-xs font-mono uppercase tracking-[0.15em] font-bold text-foreground hover:border-brand hover:text-brand transition-colors"
          >
            {view === "grid" ? <><MapIcon className="h-4 w-4" /> Show Map</> : <><LayoutGrid className="h-4 w-4" /> Show Grid</>}
          </button>
        </FadeUp>

        <p className="mt-6 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground" data-testid="listings-count">
          {properties ? `${properties.length} propert${properties.length === 1 ? "y" : "ies"} found` : "Searching…"}
        </p>

        {view === "grid" ? (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
            {(properties || []).map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            {properties && properties.length === 0 && (
              <p className="col-span-full py-16 text-center text-muted-foreground" data-testid="listings-empty">
                No properties match your filters. Try widening your search.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="listings-map-view">
            <div className="max-h-[600px] overflow-y-auto space-y-6 pr-1">
              {(properties || []).map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
            <div className="lg:sticky lg:top-28 border hairline">
              <PropertyMap lat={markers[0]?.lat ?? -4.0435} lng={markers[0]?.lng ?? 39.6682} markers={markers} zoom={11} height="600px" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
